/**
 * MetricCard - The standard summary card used on the dashboard.
 *
 * Composes:
 *   - StatTile (atom)            : the value + label
 *   - DeltaIndicator (atom)      : optional comparison badge
 *
 * Single responsibility: render a metric in a card chrome with
 * consistent padding, border, and hover affordance. Owns no domain
 * logic — formatting (currency etc.) is the parent's call before
 * passing in the `value`.
 *
 * @example
 * <MetricCard value="$12,345" label="Revenue this month"
 *             current={12345} previous={10000} deltaMode="percent" />
 */
import React from 'react';
import { StatTile, DeltaIndicator } from '../../atoms';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface MetricCardProps {
    /** Pre-formatted display value (e.g. "$12,345" or "42") */
    value: number | string;
    /** Short descriptor below the value */
    label: string;
    /**
     * When provided alongside `previous`, renders a DeltaIndicator
     * that visualizes the change. Omit both to skip the delta.
     */
    current?: number;
    previous?: number;
    /** Delta display mode — passed straight to DeltaIndicator. */
    deltaMode?: 'absolute' | 'percent';
    /** Optional suffix on the delta (e.g. " tasks", " orders") */
    deltaSuffix?: string;
    /** Click handler — when present, the card renders interactively. */
    onClick?: () => void;
    /** Optional extra classes for the outer wrapper */
    className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const MetricCard: React.FC<MetricCardProps> = ({
    value,
    label,
    current,
    previous,
    deltaMode = 'absolute',
    deltaSuffix,
    onClick,
    className,
}) => {
    const showDelta = current !== undefined && previous !== undefined;

    return (
        <div
            onClick={onClick}
            /* Interactive cards are keyboard-reachable: button semantics plus
               Enter/Space activation, mirroring a native button. */
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onKeyDown={onClick ? (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            } : undefined}
            className={[
                'metric-card',
                'flex flex-col gap-8px p-16px bg-white border border-brand-border rounded-md min-w-0',
                onClick ? 'metric-card--interactive cursor-pointer hover:border-brand-primary/40 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2' : '',
                className ?? '',
            ].join(' ')}
            data-ref="molecule-metric-card"
        >
            <StatTile value={value} label={label} size="md" />
            {showDelta && (
                <DeltaIndicator
                    current={current as number}
                    previous={previous as number}
                    mode={deltaMode}
                    suffix={deltaSuffix}
                />
            )}
        </div>
    );
};

export default MetricCard;
